import { MbscCalendarEventData } from '@akoom-tech/mobiscroll-react';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { Box, Chip, Stack, Typography } from '@mui/material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { differenceInMinutes } from 'date-fns';
import { TransformedBooking } from '../../types';
import MbscScheduleEvent from './ScheduleEventMbsc';

type SchedulerEventCardProps = {
  data: MbscCalendarEventData;
  awaitingAcceptance?: boolean;
  pinnedProviders?: {
    provider_id: number;
    name: string;
  }[];
};

type SchedulerEventToneKey =
  | 'info'
  | 'warning'
  | 'success'
  | 'error'
  | 'not_started'
  | 'default';

const CENTER_DOT = '\u00b7';
const EN_DASH = '\u2013';
const EM_DASH = '\u2014';
const AWAITING_ACCEPTANCE_TONE = {
  accent: '#B91C1C',
  background: '#FEE2E2',
  border: '#FCA5A5',
};

const SchedulerJobHoverTooltip = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 440,
    borderRadius: 12,
    padding: theme.spacing(1.35, 1.65),
    backgroundColor: '#0F172A',
    boxShadow: `0 18px 48px ${alpha(theme.palette.common.black, 0.24)}`,
  },
}));

const normalizeToneKey = (
  value?: string | null
): SchedulerEventToneKey | null => {
  const normalized = value
    ?.trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  if (!normalized) return null;

  switch (normalized) {
    case 'info':
    case 'in_progress':
    case 'started':
      return 'info';
    case 'warning':
    case 'on_the_way':
      return 'warning';
    case 'success':
    case 'completed':
    case 'done':
    case 'finished':
      return 'success';
    case 'error':
    case 'cancelled':
    case 'canceled':
    case 'void':
      return 'error';
    case 'not_started':
    case 'not_yet_started':
    case 'upcoming':
    case 'pending':
    case 'confirmed':
    case 'new':
    case 'scheduled':
    case 'booked':
      return 'not_started';
    case 'default':
      return 'default';
    default:
      return null;
  }
};

const resolveToneKey = (
  bookingData?: Partial<TransformedBooking>
): SchedulerEventToneKey => {
  const statusTone = [
    bookingData?.statusValue,
    bookingData?.statusDisplay,
    bookingData?.statusStyleClass,
    bookingData?.cssClass,
  ]
    .map(normalizeToneKey)
    .find((tone): tone is SchedulerEventToneKey => Boolean(tone));

  return statusTone ?? 'default';
};

const toDateSafe = (
  value: Date | string | number | null | undefined
): Date | null => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolveDelayDisplay = (mins: number | null | undefined) => {
  if (!Number.isFinite(mins) || mins === null) return null;
  const rounded = Math.max(0, Math.round(mins || 0));
  const hours = Math.floor(rounded / 60);
  const remainingMinutes = rounded % 60;

  if (hours > 0) {
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }

  return `${rounded}m`;
};

const formatDurationLabel = (minutes: number) => {
  const roundedMinutes = Math.max(0, Math.round(minutes));
  if (!roundedMinutes) return null;

  const hours = roundedMinutes / 60;
  const roundedHours = Math.round(hours * 100) / 100;
  const displayHours = Number.isInteger(roundedHours)
    ? `${roundedHours}`
    : roundedHours.toFixed(2).replace(/\.?0+$/, '');

  return `${displayHours}h`;
};

const formatDisplayText = (value?: string | null) => {
  const normalized = value?.trim().replace(/[_-]+/g, ' ');

  if (!normalized) return null;

  return normalized
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatTooltipTime = (
  value: Date | string | number | null | undefined
) => {
  if (!value) return 'N/A';

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? 'N/A'
      : `${value.getHours().toString().padStart(2, '0')}:${value
          .getMinutes()
          .toString()
          .padStart(2, '0')}`;
  }

  const stringValue = String(value);
  const meridiemTimeMatch = stringValue.match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AP]M)$/i
  );
  if (meridiemTimeMatch) {
    const [, hours, minutes, meridiem] = meridiemTimeMatch;
    const parsedHours = Number(hours);
    const normalizedHours =
      meridiem.toUpperCase() === 'PM'
        ? parsedHours === 12
          ? 12
          : parsedHours + 12
        : parsedHours === 12
        ? 0
        : parsedHours;

    return `${normalizedHours.toString().padStart(2, '0')}:${minutes}`;
  }

  const isoTimeMatch = stringValue.match(/T(\d{2}):(\d{2})/);
  if (isoTimeMatch) {
    return `${isoTimeMatch[1]}:${isoTimeMatch[2]}`;
  }

  const timeMatch = stringValue.match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    const [, hours, minutes] = timeMatch;
    return `${hours.padStart(2, '0')}:${minutes}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }

  return `${parsed.getHours().toString().padStart(2, '0')}:${parsed
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
};

const getTooltipJobTitle = (
  data: MbscCalendarEventData,
  bookingData?: Partial<TransformedBooking>
) => {
  const title = data.title || bookingData?.title || `#${bookingData?.id}`;
  const status =
    bookingData?.statusDisplay ??
    formatDisplayText(bookingData?.statusValue) ??
    formatDisplayText(bookingData?.statusStyleClass);

  return [title, status].filter(Boolean).join(` ${CENTER_DOT} `);
};

const getTooltipServiceLine = (bookingData?: Partial<TransformedBooking>) => {
  const serviceDisplay =
    bookingData?.serviceDisplay ??
    bookingData?.serviceLabelDescription ??
    bookingData?.serviceLabel;
  const recurrenceDisplay = bookingData?.recurrenceDisplay;

  return [serviceDisplay, recurrenceDisplay]
    .filter(Boolean)
    .join(` ${EM_DASH} `);
};

const getTooltipAddressLine = (bookingData?: Partial<TransformedBooking>) => {
  const address = bookingData?.address;
  if (!address) return null;

  return [address.line1, address.line2, address.city, address.postcode]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
};

export default function SchedulerEventCard({
  data,
  awaitingAcceptance = false,
  pinnedProviders = [],
}: SchedulerEventCardProps) {
  const theme = useTheme();
  const bookingData = data.original as Partial<TransformedBooking> | undefined;
  const toneKey = resolveToneKey(bookingData);

  const tones: Record<
    SchedulerEventToneKey,
    { accent: string; background: string; border: string }
  > = {
    info: {
      accent: '#2F6FED',
      background: '#DBEAFE',
      border: '#3B82F6',
    },
    warning: {
      accent: '#F59E0B',
      background: '#FEF3C7',
      border: '#F59E0B',
    },
    success: {
      accent: '#22C55E',
      background: '#DCFCE7',
      border: '#22C55E',
    },
    error: {
      accent: '#EF4444',
      background: '#FEE2E2',
      border: '#F87171',
    },
    not_started: {
      accent: '#475569',
      background: '#E2E8F0',
      border: '#64748B',
    },
    default: {
      accent: '#94A3B8',
      background: '#E8EEF7',
      border: '#CBD5E1',
    },
  };

  const tone = tones[toneKey];
  const displayTone = awaitingAcceptance ? AWAITING_ACCEPTANCE_TONE : tone;
  const scheduledStart = toDateSafe(
    data.start as Date | string | number | undefined
  );
  const scheduledEnd = toDateSafe(
    data.end as Date | string | number | undefined
  );
  const totalMinutesFromRange =
    scheduledStart && scheduledEnd
      ? differenceInMinutes(scheduledEnd, scheduledStart)
      : null;
  const totalMinutesFromTotalHours =
    typeof bookingData?.totalHours === 'number' && bookingData.totalHours > 0
      ? bookingData.totalHours * 60
      : null;
  const totalMinutes =
    Number.isFinite(totalMinutesFromRange) &&
    (totalMinutesFromRange as number) > 0
      ? (totalMinutesFromRange as number)
      : Number.isFinite(totalMinutesFromTotalHours)
      ? (totalMinutesFromTotalHours as number)
      : 0;
  const startDelayMinutes = bookingData?.startDelayMinutes ?? null;
  const hasStartDelay =
    typeof startDelayMinutes === 'number' &&
    startDelayMinutes > 5 &&
    Number.isFinite(totalMinutes) &&
    totalMinutes > 0;
  const startDelayPercent = hasStartDelay
    ? Math.min(Math.max((startDelayMinutes / totalMinutes) * 100, 0), 100)
    : 0;
  const startDelayLabelPercent = hasStartDelay
    ? Math.min(Math.max(startDelayPercent, 12), 42)
    : 0;
  const contentPaddingLeft = hasStartDelay
    ? `${startDelayLabelPercent + 2}%`
    : undefined;
  const startDelayDisplay = resolveDelayDisplay(startDelayMinutes);
  const durationLabel = formatDurationLabel(totalMinutes);
  const timeRangeLabel = `${formatTooltipTime(data.start)}-${formatTooltipTime(
    data.end
  )}`;
  const tooltipTimeRangeLabel = `${formatTooltipTime(
    data.start
  )}${EN_DASH}${formatTooltipTime(data.end)}`;
  const tooltipJobTitle = getTooltipJobTitle(data, bookingData);
  const tooltipTimeLine = `${tooltipTimeRangeLabel}${
    durationLabel ? ` (${durationLabel})` : ''
  } ${CENTER_DOT} ${bookingData?.address?.postcode || 'N/A'}`;
  const tooltipServiceLine = getTooltipServiceLine(bookingData);
  const tooltipAddressLine = getTooltipAddressLine(bookingData);

  return (
    <SchedulerJobHoverTooltip
      title={
        <Stack spacing={0.55} data-testid="scheduler-job-hover-tooltip">
          <Typography
            component="div"
            fontSize={15}
            fontWeight={800}
            lineHeight={1.2}
            color="common.white"
          >
            {tooltipJobTitle}
          </Typography>
          <Typography
            component="div"
            fontSize={13.5}
            lineHeight={1.25}
            color="grey.300"
          >
            {tooltipTimeLine}
          </Typography>
          {tooltipAddressLine && (
            <Typography
              component="div"
              fontSize={13.5}
              lineHeight={1.25}
              color="grey.300"
            >
              {tooltipAddressLine}
            </Typography>
          )}
          {tooltipServiceLine && (
            <Typography
              component="div"
              fontSize={13.5}
              lineHeight={1.25}
              sx={{ color: '#93C5FD' }}
            >
              {tooltipServiceLine}
            </Typography>
          )}
          {hasStartDelay && (
            <Typography
              component="div"
              fontSize={13.5}
              lineHeight={1.25}
              sx={{ color: '#FCA5A5' }}
            >
              Running {startDelayDisplay} late
            </Typography>
          )}
        </Stack>
      }
      placement="bottom"
      enterDelay={150}
      leaveDelay={60}
      followCursor
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 12],
            },
          },
        ],
      }}
      disableInteractive
    >
      <Box
        data-testid="scheduler-event-card"
        data-scheduler-status-tone={toneKey}
        sx={{
          height: '100%',
          minHeight: 64,
          border: `1px solid ${displayTone.border}`,
          borderLeft: `4px solid ${displayTone.accent}`,
          borderRadius: 1,
          backgroundColor: displayTone.background,
          boxShadow: `0 1px 0 ${alpha(theme.palette.common.black, 0.04)}`,
          pt: 0.9,
          pb: 0.75,
          pr: 1,
          pl: contentPaddingLeft ?? 1,
          position: 'relative',
          overflow: 'hidden',
          transition: theme.transitions.create(['box-shadow', 'border-color'], {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(displayTone.accent, 0.18)}`,
            borderColor: displayTone.accent,
          },
          ...(awaitingAcceptance && {
            borderStyle: 'dashed',
            borderWidth: 2,
            borderLeftWidth: 4,
          }),
        }}
      >
        {hasStartDelay && (
          <Box
            data-testid="scheduler-late-start-stub"
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            <Box
              data-testid="scheduler-late-start-shaded-area"
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: `${startDelayPercent}%`,
                minWidth: 24,
                maxWidth: '42%',
                backgroundColor: alpha(theme.palette.common.white, 0.94),
                backgroundImage: `repeating-linear-gradient(45deg, ${alpha(
                  theme.palette.grey[600],
                  0.12
                )} 0px, ${alpha(
                  theme.palette.grey[600],
                  0.12
                )} 6px, transparent 6px, transparent 12px)`,
                borderRight: `1px dashed ${alpha(
                  theme.palette.grey[700],
                  0.55
                )}`,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: `${startDelayLabelPercent}%`,
                minWidth: 28,
                maxWidth: '42%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                px: 0.25,
              }}
            >
              <Typography
                fontSize={11}
                fontWeight={800}
                color="error.dark"
                sx={{ lineHeight: 1 }}
              >
                +{startDelayDisplay ?? Math.round(startDelayMinutes ?? 0)}
              </Typography>
              <Typography
                fontSize={10}
                fontWeight={700}
                color="error.dark"
                sx={{ lineHeight: 1 }}
              >
                late
              </Typography>
            </Box>
          </Box>
        )}

        <MbscScheduleEvent
          data={data}
          awaitingAcceptance={awaitingAcceptance}
          durationLabel={durationLabel}
          timeRangeLabel={timeRangeLabel}
        />

        {pinnedProviders.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
            {pinnedProviders.map((provider) => (
              <Chip
                key={provider.provider_id}
                label={provider.name}
                size="small"
                icon={<BookmarkIcon fontSize="inherit" />}
                color="info"
                sx={{ height: 20, fontSize: 11 }}
              />
            ))}
          </Stack>
        )}
      </Box>
    </SchedulerJobHoverTooltip>
  );
}
